// components/DocumentPreviewModal.tsx
import React, { useEffect } from 'react';
import { ExternalLink, FileText, X } from '@/components/ui/lucide-memo';

interface DocumentPreviewModalProps {
  url: string;
  title: string;
  onClose: () => void;
}

const ICON_BUTTON = 'inline-flex h-7 w-7 items-center justify-center rounded text-white/55 hover:bg-white/10 hover:text-white';

/** Reads a PDF in place, so checking an invoice never leaves the table it was opened from. */
const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({ url, title, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#2A2A2A]/70 backdrop-blur-sm p-6" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="flex h-full max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-[24px] border border-white/[0.06] bg-[#212121] shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex h-11 shrink-0 items-center gap-2 border-b border-white/5 px-3">
          <FileText size={14} className="shrink-0 text-white/45" />
          <span className="min-w-0 flex-1 truncate text-sm text-white/85">{title}</span>
          <a href={url} target="_blank" rel="noreferrer" className={ICON_BUTTON} title="Open in a new tab" aria-label="Open in a new tab">
            <ExternalLink size={14} />
          </a>
          <button type="button" onClick={onClose} className={ICON_BUTTON} title="Close" aria-label="Close">
            <X size={15} />
          </button>
        </div>
        <iframe title={title} src={url} className="min-h-0 w-full flex-1 bg-white" />
      </div>
    </div>
  );
};

export default DocumentPreviewModal;
